import React from "react";
import { useDispatch } from "react-redux";
import { Paper } from "@material-ui/core";
import {
  KSelectAutocomplete,
  KSelectMulti,
  KTextInput,
} from "@kyburz/kdesign-system";

import {
  hardwareTypes,
  queclinkHardwareTypes,
} from "../../../../utils/hardwareTypes";
import { setAlert } from "../../../../redux/actions/actions";
import { useStyles } from "./styles";
import { FirmwareType, fwTypeOptions } from "../../typings";

interface Props {
  firmware: FirmwareType;
  setFirmware: (param: Partial<FirmwareType>) => void;
  selectedFileName?: string;
  setSelectedFileName?: (name: string) => void;
}

const FirmwareEditingTab = ({
  firmware,
  setFirmware,
  selectedFileName,
  setSelectedFileName,
}: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setFirmware({ [e.target.name]: e.target.value });
  };

  // A Lua script cannot go to Queclink hardware - the API refuses that pairing
  // outright (flotilla_api api/admin.go). Filtering the list is not enough on its
  // own: picking "Lua script" first and the hardware second would leave the stale
  // value selected and still submittable, so switching to Queclink hardware also
  // resets the artifact type.
  const luaOption = fwTypeOptions.find((option) => option.value === "lua");
  const isQueclinkHardware = queclinkHardwareTypes.includes(
    firmware?.hardwareType,
  );
  const availableFwTypeOptions = isQueclinkHardware
    ? fwTypeOptions.filter((option) => option.value !== "lua")
    : fwTypeOptions;

  const handleSelectChange = (name, value) => {
    const changes: Partial<FirmwareType> = { [name]: value?.value };
    if (
      name === "hardwareType" &&
      queclinkHardwareTypes.includes(value?.value) &&
      firmware?.firmwareType === "lua"
    ) {
      const fallback = fwTypeOptions[0];
      changes.firmwareType = fallback.value;
      dispatch(
        setAlert(
          `${luaOption?.label ?? "Lua script"} is not available for ` +
            `${value?.value} hardware, ` +
            `so FW Type was reset to ${fallback.label}.`,
          "warning",
        ),
      );
    }
    setFirmware(changes);
  };

  const toBase64 = (event) =>
    new Promise((resolve, reject) => {
      const file = event?.target?.files[0];
      setSelectedFileName?.(file?.name);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
        setFirmware({ contentsBase64: reader?.result as string });
      };
      reader.onerror = (error) => reject(error);
    });

  const onTypeTag = (e, tags) => {
    const { value } = e.target;
    if (value.endsWith(",")) {
      const newTag = value.trim().split(",")[0];
      setFirmware({
        tags: Array.from(new Set([...tags, newTag])),
      });
    }
  };

  const onDeleteTag = (e, tags) => {
    const value = e.option.name;
    setFirmware({
      tags: tags.filter((t) => t !== value),
    });
  };

  return (
    <Paper className={classes.paper}>
      <KTextInput
        label="Name"
        name="version"
        value={firmware?.version}
        onChange={handleChange}
        placeholder="Name"
        width="100%"
        required
      />
      <KSelectAutocomplete
        label="Hardware"
        placeholder="Select Hardware"
        style={{ width: "100%" }}
        value={firmware?.hardwareType}
        elements={hardwareTypes}
        onChange={handleSelectChange}
        name="hardwareType"
      />
      <KSelectAutocomplete
        key={`fwType-${firmware?.firmwareType ?? ""}`}
        label="FW Type"
        placeholder="Select FW Type"
        style={{ width: "100%" }}
        value={
          fwTypeOptions.find(
            (option) => option.value === firmware?.firmwareType,
          )?.label
        }
        elements={availableFwTypeOptions}
        onChange={handleSelectChange}
        name="firmwareType"
      />
      <KTextInput
        label="Description"
        name="description"
        value={firmware?.description}
        onChange={handleChange}
        placeholder="Description"
        width="100%"
      />
      <KSelectMulti
        key="tags"
        name="Tags"
        placeholder="Tags"
        label="Tags"
        elements={[]}
        noOptionsText="Type comma separated tag names"
        defaultSelected={firmware?.tags.map((t) => ({ name: t }))}
        onType={(e) => onTypeTag(e, firmware?.tags)}
        onChange={(_name, _value, e) => onDeleteTag(e, firmware?.tags)}
        width="100%"
      />
      <div style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 14 }}>Config File *</div>
        <label
          id="file-input-label"
          htmlFor="file-input"
          className={classes.fileInputLabel}
        >
          Select a File
          <input
            type="file"
            id="file-input"
            name="file-input"
            onChange={(event) => toBase64(event)}
            className={classes.fileInput}
          />
        </label>
        <div className={classes.fileName}>{selectedFileName}</div>
      </div>
    </Paper>
  );
};

export default FirmwareEditingTab;
